import { useBunja } from 'bunja/react';
import { useAtomValue, useSetAtom, useStore } from 'jotai';

import { ClearableNumberInputWithFrame } from '@flipster/features/src/input/clearable-number-input';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { stack } from '@flipster/styled-system/patterns';
import { UsdtCurrency, getQuoteCurrencyPrecision } from '@flipster/utils/constants/currency';
import segment from '@flipster/utils/segment';
import { TradeEvent } from '@flipster/utils/segment/events/trade';

import { PositionSizeInput } from '@web/app/[locale]/(web)/trade/_misc/order-form/components/PositionSizeInput';
import { tradeSymbolBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';

import ErrorMessage from './ErrorMessage';
import { formBunja } from './bunja/form';
import { formSegmentDataBunja } from './bunja/form-segment-data';
import { displayStateBunja } from './bunja/price';
import { validationBunja } from './bunja/validation';

/**
 * 주문 크기 입력부(margin 단일 인풋 또는 positionSize 이중 인풋)와 인라인 에러.
 * orderState/orderPercent 등 값 atom을 구독하므로 keystroke마다 리렌더되는 "활성 영역"이다.
 * 반대로 헤더/슬라이더는 별도 컴포넌트로 분리돼 이 영역의 리렌더에 영향받지 않는다.
 * segment 페이로드용 값(positionMode/orderType)은 store.get으로 읽는다.
 */
const OrderSizeField = () => {
  const store = useStore();
  const { symbolInfoWithDefaultAtom } = useBunja(tradeSymbolBunja);
  const { transitKindAtom, changeOrderStateAtom, orderStateAtom, orderPercentAtom } = useBunja(formBunja);
  const { fieldValidationErrorsAtom } = useBunja(validationBunja);
  const { throttledAmountAtom, throttledQuantityAtom } = useBunja(displayStateBunja);
  const { symbol, orderTypeAtom, positionModeAtom } = useBunja(formSegmentDataBunja);

  const symbolInfo = useAtomValue(symbolInfoWithDefaultAtom);
  const transitKind = useSetAtom(transitKindAtom);
  const changeOrderState = useSetAtom(changeOrderStateAtom);
  const orderState = useAtomValue(orderStateAtom);
  const orderPercent = useAtomValue(orderPercentAtom);
  const throttledAmount = useAtomValue(throttledAmountAtom);
  const throttledQuantity = useAtomValue(throttledQuantityAtom);
  const errors = useAtomValue(fieldValidationErrorsAtom);

  const currency = symbolInfo?.positionUnit;
  const quoteCurrency = symbolInfo?.quoteCurrency ?? UsdtCurrency;
  const tradeQtyPrecision = symbolInfo?.tradeQtyPrecision;
  const quoteCurrencyPrecision = getQuoteCurrencyPrecision(quoteCurrency);

  const orderType = orderState.kind === 'quantity' ? 'qty' : 'amount';
  const error = errors?.[orderState.kind];

  const trackAmountEditClick = (order_by_token: 'margin' | 'quantity' | 'position_size') => {
    segment.track(TradeEvent.OrderAmountEditClick, {
      symbol: symbol,
      position_mode: store.get(positionModeAtom),
      order_by_token,
      order_type: store.get(orderTypeAtom),
      amount_input_type: 'manual',
    });
  };

  return (
    <div className={stack({ pos: 'relative', pb: 16, pt: 0, w: '100%', gap: 4 })}>
      {orderState.kind === 'margin' ? (
        <ClearableNumberInputWithFrame
          hideClearButton
          value={orderState.margin}
          name='margin'
          onChange={(margin) => changeOrderState('margin', margin)}
          precision={quoteCurrencyPrecision}
          min={0}
          onFocus={() => {
            if (Number(orderState.margin) === 0) changeOrderState('margin', '');
            trackAmountEditClick('margin');
          }}
          suffix={
            <>
              <Formatted.Percent
                css={{
                  textStyle: 'Text/M/Monospace/Regular',
                  py: 0,
                  px: 8,
                  color: 'content.default.level-2',
                }}
                value={orderPercent}
                precision={0}
              />
              <span className={css({ textStyle: 'Text/L/Regular' })} translate='no'>
                {quoteCurrency}
              </span>
            </>
          }
        />
      ) : (
        <PositionSizeInput
          percent={orderPercent}
          currency={currency}
          quoteCurrency={quoteCurrency}
          tradeQtyPrecision={tradeQtyPrecision}
          focusTarget={orderType}
          qtyValue={orderState.kind === 'quantity' ? orderState.quantity : throttledQuantity}
          amountValue={orderState.kind === 'amount' ? orderState.amount : throttledAmount}
          onChangeFocus={(val) => {
            if (val === 'qty') {
              transitKind('quantity');
              if (Number(orderState.kind === 'quantity' ? orderState.quantity : throttledQuantity) === 0) {
                changeOrderState('quantity', '');
              }
            }
            if (val === 'amount') {
              transitKind('amount');
              if (Number(orderState.kind === 'amount' ? orderState.amount : throttledAmount) === 0) {
                changeOrderState('amount', '');
              }
            }
            trackAmountEditClick(val === 'qty' ? 'quantity' : 'position_size');
          }}
          onChangeQtyInput={(q) => changeOrderState('quantity', q)}
          onChangeAmountInput={(a) => changeOrderState('amount', a)}
        />
      )}
      {error && <ErrorMessage message={error.message} action={error.action} />}
    </div>
  );
};

export default OrderSizeField;
